import { useCallback, useState } from 'react';
import { PhotoState, PhotoIssueTypeInput, useUpdatePhotoIssuesMutation } from 'data/__generated__';

/** What the photo belongs to: exactly one of the three is set. */
type PhotoOwner = {
    deliveryId: string | null;
    setupInstructionId: string | null;
    taskInstructionId: string | null;
};

const NO_OWNER: PhotoOwner = { deliveryId: null, setupInstructionId: null, taskInstructionId: null };
const NO_ISSUE: PhotoIssueTypeInput = { isBadQuality: false, isBadQuantity: false };

export const usePhotoActions = (
    getPhotoMetadata?: (
        url: string,
    ) => { type: string; photoIndex: number; state?: PhotoState | null; isMandatory?: boolean } | undefined,
) => {
    const [updatePhotoIssues] = useUpdatePhotoIssuesMutation();
    const [popupState, setPopupState] = useState<PhotoOwner & { isOpen: boolean; url: string | null }>({
        ...NO_OWNER,
        isOpen: false,
        url: null,
    });

    const handlePhotoAction = useCallback(
        async ({
            owner,
            url,
            newState,
            issueType,
            comment,
        }: {
            owner: PhotoOwner;
            url: string;
            newState: PhotoState;
            issueType: PhotoIssueTypeInput;
            comment?: string | null;
        }) => {
            const metadata = getPhotoMetadata?.(url);

            const currentState = metadata?.state;
            const finalState = currentState === newState ? PhotoState.Pending : newState;

            const input = {
                ...owner,
                url,
                type: metadata?.type ?? 'unknown',
                photoIndex: metadata?.photoIndex ?? 0,
                state: finalState,
                issueType,
                isMandatory: metadata?.isMandatory ?? false,
                comment: comment ?? null,
            };

            await updatePhotoIssues({ variables: { controlPhoto: input } });
        },
        [updatePhotoIssues, getPhotoMetadata],
    );

    const closeIssuePopup = useCallback(() => {
        setPopupState({ ...NO_OWNER, isOpen: false, url: null });
    }, []);

    const handleIssueTypeSelection = useCallback(
        (issueType: PhotoIssueTypeInput, comment: string) => {
            const { isOpen, url, ...owner } = popupState;
            if (isOpen && url) {
                handlePhotoAction({ owner, url, newState: PhotoState.Refused, issueType, comment });
            }
            closeIssuePopup();
        },
        [popupState, handlePhotoAction, closeIssuePopup],
    );

    const createActions = useCallback(
        (owner: PhotoOwner) => ({
            onValidate: (url: string) => {
                handlePhotoAction({ owner, url, newState: PhotoState.Validated, issueType: NO_ISSUE, comment: null });
            },
            onRefuse: (url: string) => {
                if (getPhotoMetadata?.(url)?.state === PhotoState.Refused) {
                    handlePhotoAction({ owner, url, newState: PhotoState.Refused, issueType: NO_ISSUE, comment: null });
                } else {
                    setPopupState({ ...owner, isOpen: true, url });
                }
            },
        }),
        [handlePhotoAction, getPhotoMetadata],
    );

    const createInstructionActions = useCallback(
        (setupInstructionId: string) => createActions({ ...NO_OWNER, setupInstructionId }),
        [createActions],
    );

    const createDeliveryActions = useCallback(
        (deliveryId: string) => createActions({ ...NO_OWNER, deliveryId }),
        [createActions],
    );

    const createTaskInstructionActions = useCallback(
        (taskInstructionId: string) => createActions({ ...NO_OWNER, taskInstructionId }),
        [createActions],
    );

    return {
        createInstructionActions,
        createDeliveryActions,
        createTaskInstructionActions,
        popupState,
        handleIssueTypeSelection,
        closeIssuePopup,
    };
};
